import 'core-js/modules/es.array.concat';
import 'core-js/modules/es.array.filter';
import 'core-js/modules/es.array.find-index';
import 'core-js/modules/es.array.includes';
import 'core-js/modules/es.array.join';
import 'core-js/modules/es.array.map';
import 'core-js/modules/es.array.slice';
import 'core-js/modules/es.array.splice';
import 'core-js/modules/es.regexp.exec';
import 'core-js/modules/es.string.includes';
import 'core-js/modules/es.string.replace';
import 'core-js/modules/es.string.split';
import 'core-js/modules/web.dom-collections.for-each';
import { isWorldReadyInstance } from '@paypalcorp/worldready-core';
import { assert, assertType, assertRange } from 'yaassertion';

/**
 * Format Message Parts.
 *
 * Given messageParts [1] and variables [2] below, returns [3].
 * 1: [{type: "literal", value: "Hi, "}, {type: "variable", value: "name"}]
 * 2: {name: "John"}, becomes
 * 3: [{type: "literal", value: "Hi, "}, {type: "variable", variableName: "name", value: "John"}]
 *
 * Given messageParts [1] and variables [2] below, returns [3].
 * 1:
 * [
 *  { type: "literal", value: "Transfer " },
 *  {
 *    type: "variable-block",
 *    value: "bold",
 *    children: [{ type: "literal", value: "money" }]
 *  }
 * ]
 * 2: {bold: innerParts => <b>${innerParts.map(part => part.value)}</b>}, becomes
 * 3:
 * [
 *  { type: "literal", value: "Transfer " },
 *  {
 *    type: "variable-block",
 *    variableName: "bold",
 *    value: innerParts => <b>${innerParts.map(part => part.value)}</b>,
 *    children: [{ type: "literal", value: "money" }]
 *  }
 * ]
 */
function _formatToParts(messageParts, variables) {
  if (variables === void 0) {
    variables = {};
  }
  return messageParts.map(function (part) {
    var type = part.type,
      value = part.value,
      children = part.children;
    if (type === "variable") {
      return {
        type: type,
        value: variables[value],
        variableName: value
      };
    }
    if (type === "variable-block") {
      return {
        type: type,
        value: variables[value],
        children: _formatToParts(children, variables),
        variableName: value
      };
    }
    return part;
  });
}
function isPart(part) {
  var ret = true;
  var type = part.type,
    children = part.children;

  // If part has children, it's a part only if all children are also parts.
  if (children) {
    ret &= Array.isArray(children) && children.every(function (part) {
      return isPart(part);
    });
  }

  // Part must be an object that includes a string type and a value.
  ret &= typeof part === "object" && "type" in part && "value" in part && typeof type === "string";
  return ret;
}
function isMessage(node) {
  /**
   * If content is of form:
   * step[1]="step 1"
   * step[2]="step 2"
   * then message structure is [null, "step 1", "step 2"]
   * hence node can be null
   */
  if (!node || typeof node === "string" || typeof node === "symbol") {
    return true;
  }
  if (typeof node === "object" && "_message" in node && Array.isArray(node._message) && node._message.every(function (part) {
    return isPart(part);
  })) {
    return true;
  }
  return false;
}
var DEPRECATED_MESSAGE_MISSING = "  ";
var MESSAGE_MISSING = "\uE007\uE007";
var WORLDREADY_EXPERIENCE_KEY_PREFIX = "WORLDREADY";
function getWorldReadyExperienceId(market, id) {
  return [WORLDREADY_EXPERIENCE_KEY_PREFIX, market].concat(id);
}
function isMessageValueMissing(val) {
  // TODO: Remove DEPRECATED_MESSAGE_MISSING
  return val && (val === MESSAGE_MISSING || val === DEPRECATED_MESSAGE_MISSING);
}

// Internal imports.
function flattenToNestedParts(flatenParts) {
  var nestedParts = [].concat(flatenParts);
  var message = flatenParts.map(function (_ref) {
    var type = _ref.type,
      value = _ref.value;
    if (type === "variable-block-start") {
      return "{" + value + "}";
    }
    if (type === "variable-block-end") {
      return "{/" + value + "}";
    }
    return value;
  }).join("");
  var _loop = function _loop() {
    var _nestedParts$i = nestedParts[i],
      type = _nestedParts$i.type,
      value = _nestedParts$i.value;
    if (type === "variable-block-start") {
      var variableStartName = "{" + value + "}";
      var variableEndName = "{/" + value + "}";
      var endIndex = nestedParts.findIndex(function (_ref2) {
        var type = _ref2.type,
          thisValue = _ref2.value;
        return type === "variable-block-end" && value === thisValue;
      });
      if (process.env.NODE_ENV !== "production") {
        // TODO: fix assert to NOT replace {<occurrencies>} from `message` string.
        assert(endIndex !== -1, 'Missing {variableEndName} variable "end-tag" in {{message}}', {
          variableEndName: variableEndName,
          // TODO: assert to support fn callback to get property value.
          // message: () => nestedParts.map(({value}) => value).join("");
          message: message
        });
      }
      var newPart = {
        type: "variable-block",
        value: value
      };
      var subParts = nestedParts.splice(i, endIndex - i + 1, newPart).slice(1, -1);
      if (process.env.NODE_ENV !== "production") {
        assert(subParts.length, "Missing content for {variableStartName}{variableEndName} variable pair", {
          variableStartName: variableStartName,
          variableEndName: variableEndName
        });
      }
      newPart.children = flattenToNestedParts(subParts);
    }
  };
  for (var i = 0; i < nestedParts.length; i++) {
    _loop();
  }
  return nestedParts;
}

/**
 * Schema converter utility for client-side (browser):
 * - Needed by the inline source message feature;
 * - Only used on development;
 * - The difference between this one and schema-converter.server.js is that this
 *   one is simpler and doesn't handle HTML in inline source messages;
 */
var variableRe = /{([A-Za-z_/][0-9a-zA-Z-_]*)}/g;

/**
 * Following function breaks messages that contain variables and/or html markup into parts. For example:
 *
 * "Hi, {name}" becomes:
 * => [{type: "literal", value: "Hi, "}, {type: "variable", value: "name"}]
 *
 * "Transfer {bold}money{/bold}" becomes:
 * -> [ # before flattenToNestedParts()
 * [
 *  { type: "literal", value: "Transfer " },
 *  { type: "variable-block-start", value: "bold" },
 *  { type: "literal", value: "money" },
 *  { type: "variable-block-end", value: "/bold" }
 * ]
 * => [ # after flattenToNestedParts()
 *  { type: "literal", value: "Transfer " },
 *  {
 *    type: "variable-block",
 *    value: "bold",
 *    children: [{ type: "literal", value: "money" }]
 *  }
 * ]
 */
function messageStringToWorldReadySchema(value) {
  // If no variables, keep the string itself instead of its parts
  if (!variableRe.test(value)) {
    return value;
  }
  var parts = [];
  var currPos = 0;
  value.replace(variableRe, function (match, variableName, matchPos) {
    // Push leading literal up to the variable match.
    parts.push({
      type: "literal",
      value: value.slice(currPos, matchPos)
    });

    // Push variable.
    parts.push({
      type: "variable",
      value: variableName
    });

    // Adjust currPos.
    currPos = matchPos + match.length;
  });
  parts.push({
    type: "literal",
    value: value.slice(currPos)
  });

  // Filter empty literals.
  parts = parts.filter(function (_ref3) {
    var type = _ref3.type,
      value = _ref3.value;
    return type !== "literal" || type === "literal" && value;
  });

  // Handle variable pairs.
  parts = parts.map(function (_ref4, i) {
    var type = _ref4.type,
      value = _ref4.value;
    if (type === "variable" && value[0] !== "/") {
      var variableEndName = "/" + value;
      var endIndex = parts.findIndex(
      // Renaming is required to prevent bugs in Jest env
      function (_ref5) {
        var thisValue = _ref5.value;
        return thisValue === variableEndName;
      });
      // Yes, it's a pair, adjust it like so.
      if (endIndex > i) {
        type = "variable-block-start";
        parts.splice(endIndex, 1, {
          type: "variable-block-end",
          value: value
        });
      }
    }
    return {
      type: type,
      value: value
    };
  });
  return {
    _message: flattenToNestedParts(parts)
  };
}

// Note: This is also used by inline source message
function pseudoLocMessageParts(parts, init) {
  if (init === void 0) {
    init = true;
  }
  parts = parts.map(function (_ref6) {
    var type = _ref6.type,
      value = _ref6.value,
      children = _ref6.children;
    if (children) {
      children = pseudoLocMessageParts(children, false);
      return {
        children: children,
        type: type,
        value: value
      };
    }
    if (type === "literal") {
      value = pseudoLocString(value);
    }
    return {
      type: type,
      value: value
    };
  });
  if (init) {
    parts.unshift({
      type: "literal",
      value: pseudoLocHead()
    });
    parts.push({
      type: "literal",
      value: pseudoLocTail()
    });
  }
  return parts;
}
function pseudoLocHead() {
  return "[[";
}
var pseudoLocVowels = {
  a: "α",
  e: "ḛ",
  i: "ḭ",
  o: "ṓ",
  u: "ṵ",
  A: "À",
  E: "Ḛ",
  I: "Ḭ",
  O: "Ṏ",
  U: "Ṳ"
};
function pseudoLocString(string) {
  return string.split("").map(function (c) {
    return pseudoLocVowels[c] || c;
  }).join("");
}
function pseudoLocTail() {
  return "]]";
}

/**
 * Returns region for based on locale and region value.
 * It will retun C2 if locale has CN-xbord
 * @param {string} locale
 * @param {string} region
 * @returns string - content region value
 */
function getRegionForContent(locale, region) {
  return locale.includes("CN-xbord") ? "C2" : region;
}
function assertMessageParts(messageParts, variables) {
  if (variables === void 0) {
    variables = {};
  }
  messageParts.forEach(function (_ref7) {
    var type = _ref7.type,
      value = _ref7.value,
      children = _ref7.children;
    if (type === "variable" || type === "variable-block") {
      assert(value in variables, "Missing variable {{value}}", {
        value: value
      });
    }
    if (type === "variable-block") {
      var variableName = value;
      var variableValue = variables[variableName];
      assert(typeof variableValue === "function" && variableValue.length === 1, "Variable {{name}} expected to be a function that takes content as first argument, not {{value}}", {
        name: variableName,
        value: variableValue
      });
      assertMessageParts(children, variables);
    }
  });
}
function getMessageParts(worldReady, id, source, fallback) {
  var market = getRegionForContent(worldReady.locale, worldReady.market);
  var cldr = worldReady.globalize.cldr;
  var message = cldr.main(["marketSpecificL10n", market].concat(getWorldReadyExperienceId(market, id))) || cldr.main(["l10n"].concat(getWorldReadyExperienceId(market, id))) || cldr.main(["marketSpecificL10n", market].concat(id)) || cldr.main(["l10n"].concat(id));
  if (process.env.NODE_ENV !== "production") {
    if (message) {
      assert(isMessage(message), "Invalid `id` for MessageFormat object. Did you mean {{potentialId}}?", {
        potentialId: [].concat(id, ["<something else>"])
      });
    }
  }
  if (process.env.NODE_ENV !== "production") {
    // Inline Source Message
    if (message === undefined && source !== undefined) {
      message = messageStringToWorldReadySchema(source);
    }
  }
  var messageParts;
  if (isMessageValueMissing(message)) {
    message = undefined;
  }
  if (message === undefined) {
    if (fallback === "code") {
      messageParts = [{
        type: "missingMessage",
        value: id.join("."),
        id: id
      }];
    }
  } else if (typeof message === "string") {
    messageParts = [{
      type: "literal",
      value: message
    }];
  } else {
    messageParts = message._message;
  }
  if (process.env.NODE_ENV !== "production") {
    // Inline Source Message
    if (source && cldr.attributes.language !== "en") {
      messageParts = pseudoLocMessageParts(messageParts);
    }
  }
  return messageParts;
}

/**
 * MessageFormat(worldReady, options)
 */
var MessageFormat = /*#__PURE__*/function () {
  function MessageFormat(worldReady, options) {
    // Error Check: WorldReady Object
    if (process.env.NODE_ENV !== "production") {
      assert(isWorldReadyInstance(worldReady), "WorldReady object missing.");
      try {
        assertType(options, "options", ["array", "plain object"]);
      } catch (error) {
        var _error$attributes = error.attributes,
          parameterType = _error$attributes.parameterType,
          parameterValue = _error$attributes.parameterValue;
        // If they passed a string, they probably meant `{ id }` instead.
        if (parameterType === "string") {
          error.message += ". Did you mean `{ id: \"" + parameterValue + "\" }`? (note the object and the `id` key)";
        }
        throw error;
      }
    }
    if (Array.isArray(options)) {
      options = {
        id: options
      };
    }
    var _options = options,
      id = _options.id,
      _options$fallback = _options.fallback,
      fallback = _options$fallback === void 0 ? "code" : _options$fallback,
      source = _options.source,
      namespace = _options.namespace;
    if (process.env.NODE_ENV !== "production") {
      assertType(id, "id", ["array", "string"]);
      assertRange(fallback, ["code", "none"], "options.fallback must be 'none', or 'code'");
      if (namespace) {
        assertType(namespace, "namespace", ["string", "array"]);
      }
    }
    if (typeof id === "string") {
      id = id.split(".");
    }
    if (namespace) {
      if (typeof namespace === "string") {
        namespace = namespace.split(".");
      }
      id = [].concat(namespace, id);
    }
    if (process.env.NODE_ENV !== "production") {
      id.forEach(function (part, i) {
        return assertType(part, "id[" + i + "]", ["string"]);
      });
    }

    // TODO if server side only
    this.messageParts = getMessageParts(worldReady, id, source, fallback);
  }
  var _proto = MessageFormat.prototype;
  _proto.format = function format(variables) {
    if (!this.messageParts) return;
    var parts = this.formatToParts(variables);
    if (process.env.NODE_ENV !== "production") {
      parts.forEach(function (_ref8) {
        var type = _ref8.type;
        return assert(type !== "variable-block", "Use .formatToParts() to format messages containing `variable-block` parts");
      });
    }
    return parts.map(function (_ref9) {
      var value = _ref9.value;
      return value;
    }).join("");
  };
  _proto.formatToParts = function formatToParts(variables) {
    if (!this.messageParts) return;
    if (process.env.NODE_ENV !== "production") {
      assertType(variables, "variables", ["plain object", "undefined"]);
      assertMessageParts(this.messageParts, variables);
    }
    return _formatToParts(this.messageParts, variables);
  };
  return MessageFormat;
}();

export { MessageFormat, getMessageParts };
